import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Flame, Mic, MessageCircle, BookOpen, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "@/context/AppContext";
import { useNoIndex } from "@/hooks/useNoIndex";

const API = process.env.REACT_APP_BACKEND_URL;
const RN_HERO_SUB = "Reflection Morning · October 23, 2026 · 11:00 AM · The Canyon House, Mission Viejo. Space is limited — reserve your seat below.";

function RNTicket() {
  const [status, setStatus] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [free, setFree] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/reflection-nights`).then((r) => r.json()).then(setStatus).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { toast.error("Please add your name and email."); return; }
    setLoading(true);
    if (code.trim()) {
      try {
        const r = await fetch(`${API}/api/reflection-night/redeem`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, email, code }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) { toast.error(data.detail || "That code isn't valid."); setLoading(false); return; }
        setFree(true);
      } catch { toast.error("Something went wrong. Please try again."); }
      setLoading(false);
      return;
    }
    try {
      const r = await fetch(`${API}/api/reflection-night/checkout`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, origin_url: window.location.origin }),
      });
      if (r.status === 409) { toast.error("This Reflection Night is fully booked."); setLoading(false); return; }
      if (!r.ok) throw new Error("checkout failed");
      const data = await r.json();
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      throw new Error("no url");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const left = status?.seats_left;
  if (free) return (
    <div data-testid="rn-free-success" className="bg-[#F4EFE6] text-[#2A2825] p-8 md:p-10 w-full max-w-md rounded-2xl text-left">
      <p className="tracking-overline text-[10px] uppercase text-[#A9724E] mb-4">Seat reserved</p>
      <p className="font-serif-display text-2xl leading-snug mb-3">You're in, {name.trim().split(" ")[0]}. ✦</p>
      <p className="text-sm text-[#5E4B3C] font-body leading-relaxed">Your code covered your seat. A confirmation is on its way to {email.trim()} — see you October 23 at 11AM, The Canyon House, Mission Viejo.</p>
    </div>
  );
  return (
    <div className="bg-[#F4EFE6] text-[#2A2825] p-8 md:p-10 w-full max-w-md rounded-2xl text-left">
      <p className="tracking-overline text-[10px] uppercase text-[#A9724E] mb-1">Reflection Morning · Oct 23 · 11AM</p>
      <p className="font-serif-display text-xl mb-6">Reserve your seat · {code.trim() ? <span className="line-through text-[#8a7a6a]">$27</span> : "$27"}{code.trim() && <span className="text-[#A9724E]"> Free with code</span>}</p>
      <form onSubmit={submit} className="space-y-3" data-testid="rn-ticket-form">
        <input data-testid="rn-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="w-full bg-transparent border-b border-[#D1C9BE] h-11 px-0 focus:outline-none focus:border-[#A9724E] font-body" />
        <input data-testid="rn-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className="w-full bg-transparent border-b border-[#D1C9BE] h-11 px-0 focus:outline-none focus:border-[#A9724E] font-body" />
        {showCode ? (
          <input data-testid="rn-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Promo code" autoFocus className="w-full bg-transparent border-b border-[#D1C9BE] h-11 px-0 focus:outline-none focus:border-[#A9724E] font-body uppercase tracking-widest" />
        ) : (
          <button type="button" data-testid="rn-have-code" onClick={() => setShowCode(true)} className="text-xs text-[#A9724E] underline underline-offset-4 hover:text-[#925C3B] font-body">Have a code?</button>
        )}
        <button type="submit" disabled={loading || left === 0} data-testid="rn-reserve-button" className="w-full rounded-full bg-[#A9724E] hover:bg-[#925C3B] disabled:opacity-50 text-[#F4EFE6] px-7 py-3.5 text-sm tracking-wide transition-colors duration-500">
          {loading ? (code.trim() ? "Reserving…" : "Taking you to checkout…") : left === 0 ? "Fully booked" : code.trim() ? "Claim my free seat" : "Reserve your seat · $27"}
        </button>
        <p className="text-[11px] text-[#8a7a6a] font-body text-center">{code.trim() ? "Confirmation emailed instantly" : "Secure checkout via Stripe · confirmation emailed instantly"}</p>
      </form>
    </div>
  );
}

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}>
    {children}
  </motion.div>
);

const FLOW = [
  { icon: Flame, time: "As you arrive", title: "Gather & settle", body: "Low lighting, candles, music, and a warm welcome. Find your seat at the table, meet the people beside you, and let the day begin to slow." },
  { icon: Mic, time: "The stories", title: "Storytelling", body: "A few voices share honest, real-life stories — moments of being tipped over and poured out. Sometimes funny, sometimes tender, always human." },
  { icon: MessageCircle, time: "Together", title: "Reflection & conversation", body: "Guided questions and open space to reflect, respond, and connect. No performing, no pressure — just honest conversation around the table." },
  { icon: BookOpen, time: "The turn", title: "A quiet moment to reflect", body: "A gentle pause to sit with what surfaced — a few honest words, a shared thought, space to breathe. Take what resonates; leave what doesn't." },
  { icon: Sparkles, time: "Lingering", title: "Stay a while", body: "The night ends the way the best ones do — no one quite ready to leave. More conversation, more connection, a little more light." },
];

const SPONSOR_TYPES = ["Venue / space", "Food & drinks", "Music or live art", "Décor & candles", "Financial sponsor", "Something else"];

function SponsorForm() {
  const [form, setForm] = useState({ name: "", email: "", sponsor_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.sponsor_type) { toast.error("Please add your name, email, and what you'd like to sponsor."); return; }
    setLoading(true);
    try {
      await fetch(`${API}/api/reflection-night/sponsor`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) }).then((r) => { if (!r.ok) throw new Error(); });
      setDone(true);
    } catch { toast.error("Something went wrong. Please try again."); }
    setLoading(false);
  };
  if (done) return <p data-testid="rn-sponsor-success" className="font-serif-display italic text-2xl text-[#A9724E] leading-snug">Thank you — Dej will reach out to you personally soon. ✦</p>;
  const input = "w-full bg-transparent border-b border-[#D1C9BE] h-11 px-0 focus:outline-none focus:border-[#A9724E] font-body text-[#2A2825] placeholder:text-[#8a7a6a]";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="rn-sponsor-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="rn-sponsor-name" value={form.name} onChange={set("name")} placeholder="Your name" className={input} />
        <input data-testid="rn-sponsor-email" type="email" value={form.email} onChange={set("email")} placeholder="Email address" className={input} />
      </div>
      <div className="flex flex-wrap gap-2 pt-2" data-testid="rn-sponsor-types">
        {SPONSOR_TYPES.map((t) => (
          <button type="button" key={t} data-testid={`rn-sponsor-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setForm((f) => ({ ...f, sponsor_type: t }))}
            className={`rounded-full border px-4 py-2 text-xs tracking-wide transition-colors ${form.sponsor_type === t ? "bg-[#A9724E] border-[#A9724E] text-[#F4EFE6]" : "border-[#D1C9BE] text-[#5E4B3C] hover:border-[#A9724E]"}`}>{t}</button>
        ))}
      </div>
      <textarea data-testid="rn-sponsor-message" value={form.message} onChange={set("message")} rows={3} placeholder="Anything you'd like us to know (optional)" className={`${input} h-auto py-2 resize-none`} />
      <button type="submit" disabled={loading} data-testid="rn-sponsor-submit" className="rounded-full bg-[#2A2825] hover:bg-[#413d38] disabled:opacity-50 text-[#F4EFE6] px-7 py-3.5 text-sm tracking-wide transition-colors">
        {loading ? "Sending…" : "Let Dej know"}
      </button>
    </form>
  );
}

export default function ReflectionNightsPage() {
  useNoIndex();
  return (
    <div className="bg-[#F4EFE6] text-[#2A2825]">
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-end grain">
        <div className="absolute inset-0">
          <img src={IMAGES.reflectionSinger} alt="A candlelit Reflection Night gathering" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2A2825] via-[#2A2825]/55 to-[#2A2825]/25" />
        </div>
        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-12 pb-16 md:pb-20 pt-28">
          <p className="tracking-overline text-xs uppercase text-[#E8B486] mb-6">Live Storytelling · Open to women &amp; men · Next: Reflection Morning, Oct 23</p>
          <h1 className="font-serif-display font-light text-[#F4EFE6] text-4xl sm:text-6xl leading-[1.05] max-w-3xl">Reflection Nights</h1>
          <p className="mt-5 text-[#D9CFC2] font-body text-lg max-w-xl">Music. Real-life stories. Honest reflection. Come tell one, or come just to listen.</p>
          <p data-testid="rn-next-event" className="mt-6 inline-flex flex-wrap items-center gap-x-3 gap-y-1 border border-[#E8B486]/50 rounded-full px-5 py-2.5 text-sm font-body text-[#F4EFE6]"><span className="text-[#E8B486] tracking-overline uppercase text-[10px]">Next gathering</span> Reflection Morning · Oct 23, 2026 · 11AM · The Canyon House, Mission Viejo</p>
        </div>
      </section>

      {/* WHAT IT IS */}
      <section className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-6">What it is</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif-display font-light text-3xl md:text-5xl leading-tight mb-8">An evening of story, connection, and honest reflection.</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="space-y-5 text-[#5E4B3C] leading-relaxed font-body text-lg">
              <p>Every person carries a story. Reflection Nights are open community gatherings where strangers become friends through honest conversation, storytelling, music, and reflection.</p>
              <p>Someone tells a real story — not necessarily a sermon, and not necessarily somebody with a microphone telling you five steps to fix your life. Just: "Here's something I lived." Then we actually sit with it.</p>
              <p className="font-serif-display italic text-2xl text-[#A9724E] leading-snug">Reflection Night is: Story → Reflection → Connection → Action.</p>
              <p>Part supper club, part open mic, part exhale — it's a warm night out for women and men. Your next girls' night, date night, or solo self-care reset. Hosted by Outpoured, every evening is built around hospitality and honest connection — a place to slow down, be present, and feel a little more human by the time you leave. Never a performance, never a pressure.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* IMAGE GALLERY */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <Reveal className="col-span-2 md:col-span-2 row-span-2"><img src={IMAGES.reflection3} alt="Friends laughing together at a Reflection Night" className="w-full h-full object-cover aspect-[4/5] md:aspect-auto md:h-full" /></Reveal>
          <Reveal delay={0.08} className="col-span-2"><img src={IMAGES.reflectionSinger} alt="Storytelling by candlelight" className="w-full object-cover aspect-[16/10]" /></Reveal>
          <Reveal delay={0.14}><img src={IMAGES.reflection2} alt="Women laughing at the table" className="w-full object-cover aspect-square" /></Reveal>
          <Reveal delay={0.2}><img src={IMAGES.reflectionPaint} alt="A live painter working by candlelight during a Reflection Night" className="w-full object-cover aspect-square" /></Reveal>
        </div>
      </section>

      {/* THE FLOW OF THE NIGHT */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-4">The flow of the night</p>
            <h2 className="font-serif-display font-light text-3xl md:text-5xl leading-tight mb-14">How an evening unfolds.</h2>
          </Reveal>
          <div className="space-y-2">
            {FLOW.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="flex gap-6 md:gap-10 py-8 border-b border-[#D8CEBE]">
                  <s.icon className="w-7 h-7 text-[#A9724E] shrink-0 mt-1" strokeWidth={1.25} />
                  <div>
                    <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-1">{s.time}</p>
                    <h3 className="font-serif-display text-2xl md:text-3xl text-[#2A2825] mb-2">{s.title}</h3>
                    <p className="text-[#5E4B3C] leading-relaxed font-body">{s.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SPONSOR A NIGHT */}
      <section data-testid="rn-sponsor" className="py-24 md:py-32 bg-[#EAE3D9]/50 border-t border-[#D1C9BE]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-[1fr_minmax(0,28rem)] gap-12 md:gap-20 items-start">
          <div>
            <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-4">Sponsor a night</p></Reveal>
            <Reveal delay={0.06}><h2 className="font-serif-display font-light text-3xl md:text-5xl leading-tight mb-6">Have a space, a table, or a gift to share?</h2></Reveal>
            <Reveal delay={0.1}>
              <p className="text-[#5E4B3C] font-body leading-relaxed text-lg">Reflection Nights happen because people open their doors and their hands — a venue, a meal, music, candles, or a financial gift. If you'd like to help host one, leave your name and Dej will personally reach out.</p>
            </Reveal>
          </div>
          <Reveal delay={0.14}><SponsorForm /></Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#2A2825] text-[#F4EFE6] relative grain">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="tracking-overline text-xs uppercase text-[#E8B486] mb-6">Come to the table · Reflection Morning edition</p>
            <h2 className="font-serif-display font-light text-3xl md:text-5xl leading-tight mb-6">There's a seat here for you.</h2>
            <p className="text-[#C9BEB0] font-body leading-relaxed max-w-lg mx-auto mb-10">{RN_HERO_SUB}</p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="flex justify-center">
              <RNTicket />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
